import type { PaymentPlatform } from '@forest/paywall-core'
import { compare, validate } from 'compare-versions'

const params = new URLSearchParams(location.search)

const appVersion = params.get('app_version')
export const resolvedAppVersion = appVersion ?? '0.0.0'
export const language = params.get('language') ?? 'en'
export const intlLocale = language.replace(/_/g, '-')

function parsePlatform(value: string | null): PaymentPlatform {
  if (
    value === 'ios' ||
    value === 'googleplay_android' ||
    value === 'china_android'
  ) {
    return value
  }
  return 'ios'
}

export const platform = parsePlatform(params.get('platform'))
// Shop server clock seed (GLOSSARY.md "Shop server clock"): native puts a
// unix-seconds `ts` on every launch URL. The device clock is the last-resort
// seed for dev/preview URLs without one; a production launch missing `ts`
// breaks that guarantee, so it complains before falling back.
const tsParam = Number(params.get('ts'))
const hasValidTs = Number.isFinite(tsParam) && tsParam > 0
if (!hasValidTs && import.meta.env.PROD) {
  console.error('[shop] launch URL has no ts; server clock seeded from device')
}
export const serverTimestampSeedMs = hasValidTs ? tsParam * 1000 : Date.now()
// Launch deep-link params, raw: ~/lib/deep-link maps tab to a shop tab and
// resolves the product parameter — tree_id, or landskin_id (the l10n key
// spelling, not the `land_skin` content type) — against the loaded catalogue.
export const tabParam = params.get('tab')
export const treeIdParam = params.get('tree_id')
export const landSkinIdParam = params.get('landskin_id')
const shouldMockAPI = params.has('mock')
export const shouldUseMockAPI = import.meta.env.DEV && shouldMockAPI

// Boot-performance diagnostics (~/lib/perf): on for every non-production
// build, and on a production build when the launch URL carries ?perf. Off
// under test so console assertions stay quiet.
export const perfLogsEnabled =
  import.meta.env.MODE === 'test'
    ? false
    : import.meta.env.MODE !== 'production' || params.has('perf')

// Native paywall capability: PaywallService.openPaywall exists from app
// 5.26.0 on every platform. An explicit app_version always wins; when the
// param is absent, only dev mock mode counts as supported (fail-closed in
// production, keeps ?mock=… preview URLs working without extra params).
// A malformed version also fails closed — compare() would throw at module
// load and blank the whole shop.
export function resolveNativePaywallSupport(
  appVersion: string | null,
  mock: boolean,
): boolean {
  if (appVersion === null) return mock
  return validate(appVersion) && compare(appVersion, '5.26.0', '>=')
}

export const supportsNativePaywall = resolveNativePaywallSupport(
  appVersion,
  shouldUseMockAPI,
)

const catalogueMockScenario = {
  // Milliseconds added to every mocked classic asset / l10n call — raise it
  // to hold the grid and detail skeletons on screen.
  assetDelayMs: 0,
  balances: { coin: 6_850, gem: 368 },
  currentUserStatus: 200,
  // The daily-pick draw the mock server serves (GLOSSARY.md "Exhausted
  // pool"): 'full' is the six-pick assortment; 'exhausted' four picks, two
  // of them owned, so buying the other two lands on the Super Collector
  // card; 'empty' no products — the card at once.
  dailyPickPool: 'full' as 'empty' | 'exhausted' | 'full',
  // Fail each tree's first detail-asset attempt to show the inline error;
  // the dialog's retry then succeeds.
  detailAssetsFailOnce: false,
  // 'pending' never settles any classic slot, holding the Classic tab's
  // pending slot skeletons forever (ADR 0024), the boot skeleton giving way
  // to them only at the reveal deadline.
  classicHydration: 'settled',
  // 'missing' leaves the land skins out of shop-classic, so the Classic tab
  // shows trees with no content filter chips (GLOSSARY.md "Classic content
  // filter").
  classicLandSkins: 'present' as 'missing' | 'present',
  // How the purchase status poll after a receipt answers (GLOSSARY.md "Shop
  // IAP delivery confirmation"): 'delayed' reports 'processing' for the
  // first 2 seconds, then 'completed'; 'undelivered' never completes, so the
  // 30-second deadline opens Confirming Purchase.
  iapDelivery: 'delayed' as 'delayed' | 'undelivered',
  iapPurchase: 'completed',
  // 'single' serves one main product, showing the lone hero card filling
  // the Featured tab's hero row.
  mainProducts: 'all',
  plusState: 'available',
  // 'eligible' serves the shop-starter-pack collection with one unowned pack,
  // three days from its eligible_until (GLOSSARY.md "Starter pack"); the
  // offer dialog shows once per browser. 'after-purchase' serves it only
  // once a purchase has committed, so the post-purchase refetch brings the
  // card and its offer dialog mid-session.
  starterPack: 'none' as 'after-purchase' | 'eligible' | 'none',
  // 'eligible' gives every shop-gem pack an eligible first-purchase offer
  // whose bonus item type is the pack's own, so each card shows the ×2 tag
  // and its grant doubled (GLOSSARY.md "First-purchase bonus (首購翻倍)").
  firstPurchaseBonus: 'none' as 'eligible' | 'none',
  // Today's refresh count, seeding the refresh button's tier; 'failed' fails
  // the stats read so the button hides (GLOSSARY.md "Refresh count").
  refreshCount: 0 as number | 'failed',
  // 'missing' leaves shop-daily-refresh out of the collections response,
  // hiding the refresh button (GLOSSARY.md "Refresh ticket").
  refreshTickets: 'present' as 'missing' | 'present',
  // 'pending' never settles currency hydration or trial eligibility, holding
  // the Coins & Crystals tab's sectioned skeletons and Plus CTA skeleton
  // forever (ADR 0023).
  topUpHydration: 'settled',
  trialEligible: true,
} as const

const mockScenarios = {
  catalogue: catalogueMockScenario,
  get: { ...catalogueMockScenario, trialEligible: false },
  active: { ...catalogueMockScenario, plusState: 'active' },
  // Too poor for anything: the unaffordable tint and the direct top-up
  // navigation on the tree detail, the land-skin detail, and the Daily Picks
  // refresh button, whose 300-coin first tier outruns these 200 coins. Pair
  // it with ?mock=refresh-terminal's count to reach the Time Crystal tier.
  insufficient: {
    ...catalogueMockScenario,
    balances: { coin: 200, gem: 20 },
  },
  processing: { ...catalogueMockScenario, iapPurchase: 'pending' },
  undelivered: { ...catalogueMockScenario, iapDelivery: 'undelivered' },
  error: { ...catalogueMockScenario, currentUserStatus: 503 },
  slow: { ...catalogueMockScenario, assetDelayMs: 2500 },
  'broken-detail': {
    ...catalogueMockScenario,
    assetDelayMs: 800,
    detailAssetsFailOnce: true,
  },
  'pending-top-up': { ...catalogueMockScenario, topUpHydration: 'pending' },
  'pending-classic': { ...catalogueMockScenario, classicHydration: 'pending' },
  'classic-trees-only': {
    ...catalogueMockScenario,
    classicLandSkins: 'missing',
  },
  'single-hero': { ...catalogueMockScenario, mainProducts: 'single' },
  'super-collector': { ...catalogueMockScenario, dailyPickPool: 'empty' },
  'exhausted-pool': { ...catalogueMockScenario, dailyPickPool: 'exhausted' },
  // The terminal refresh tier: five refreshes bought today.
  'refresh-terminal': { ...catalogueMockScenario, refreshCount: 5 },
  'refresh-count-failed': { ...catalogueMockScenario, refreshCount: 'failed' },
  'refresh-no-tickets': { ...catalogueMockScenario, refreshTickets: 'missing' },
  'starter-pack': { ...catalogueMockScenario, starterPack: 'eligible' },
  'starter-pack-after-purchase': {
    ...catalogueMockScenario,
    starterPack: 'after-purchase',
  },
  'first-purchase-bonus': {
    ...catalogueMockScenario,
    firstPurchaseBonus: 'eligible',
  },
} as const

type MockScenario = keyof typeof mockScenarios

function parseMockScenario(value: string | null): MockScenario {
  if (value && Object.prototype.hasOwnProperty.call(mockScenarios, value)) {
    return value as MockScenario
  }
  return 'catalogue'
}

export const mockScenarioConfig =
  mockScenarios[parseMockScenario(params.get('mock'))]
